import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { X, Phone, Loader2, ArrowLeft, Mail, Lock, Eye, EyeOff, ShieldCheck, CheckCircle2, AlertCircle, HelpCircle } from "lucide-react";
import { useAuth, startGoogleLogin } from "./AuthContext";
import { getFirebaseAuth, getRecaptchaVerifier, resetRecaptcha, signInWithPhoneNumber, isPhoneAuthConfigured } from "../lib/firebaseClient";
import useBrandAsset from "../lib/useBrandAsset";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RECAPTCHA_ID = "jns-recaptcha-container";

// Module-level cache of admin-configured sign-in methods. We fetch this once
// per page load and reuse it on subsequent modal opens so we never flash a
// disabled button.
let _authMethodsCache = null;

(function preloadAuthMethods() {
    if (_authMethodsCache) return;
    try {
        axios.get(`${API}/settings/public`).then(r => {
            const m = Array.isArray(r.data?.auth_methods) ? r.data.auth_methods : null;
            _authMethodsCache = (m && m.length) ? m : ["google", "phone"];
        }).catch(() => { /* leave null; modal will fall back */ });
    } catch { /* noop */ }
})();

const safeDetail = (e, fallback) => {
    const d = e?.response?.data?.detail;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).filter(Boolean).join(" · ") || fallback;
    if (d?.msg) return d.msg;
    return fallback;
};

const isValidEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((s || "").trim());

/**
 * Unified sign-in modal — Email/Password + Google (+ Phone OTP if enabled).
 *
 * Stages:
 *   login              — default. Email+pwd form, Google button, link to register/forgot.
 *   register           — name, email, password, confirm password, optional phone.
 *   register_verify    — 6-digit code sent to registration email.
 *   forgot             — email input.
 *   forgot_verify      — 6-digit code sent for password reset.
 *   forgot_reset       — new password + confirm.
 *   phone | otp | merge | profile  — legacy phone-OTP stages (dormant unless admin re-enables).
 */
export default function AuthOptionsModal({ open, onClose }) {
    const { refresh, user, logout } = useAuth();
    const brandLogo = useBrandAsset("brand_logo");
    const [stage, setStage] = useState("login");
    const [contactEmail, setContactEmail] = useState("");
    const [helpOpen, setHelpOpen] = useState(false);

    // Inline banner messages — replace ALL toast notifications inside the modal
    // so customer-facing feedback always lives next to the form, not as a
    // floating popup.
    const [formError, setFormError] = useState("");
    const [formFlash, setFormFlash] = useState("");  // success/info banner
    const clearBanners = () => { setFormError(""); setFormFlash(""); };
    // Whenever the stage changes, wipe any leftover banner so the new screen
    // starts clean. Pages set their own messages on submit/validation errors.
    useEffect(() => { clearBanners(); }, [stage]);

    // -- Phone OTP state (existing, kept for when Firebase setting is re-enabled) --
    const [phone, setPhone] = useState("");
    const [otp, setOtp] = useState("");
    const [sending, setSending] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);
    const confirmRef = useRef(null);
    const [authMethods, setAuthMethods] = useState(_authMethodsCache);
    const [mergeCandidate, setMergeCandidate] = useState(null);
    const [mergeChoosing, setMergeChoosing] = useState(false);
    const pendingIdTokenRef = useRef(null);
    const [signup, setSignup] = useState({ name: "", email: "", address_line: "", city: "", state: "", pincode: "" });
    const [savingProfile, setSavingProfile] = useState(false);

    // -- Email/password login state --
    const [loginForm, setLoginForm] = useState({ email: "", password: "" });
    const [showPwd, setShowPwd] = useState(false);
    const [loggingIn, setLoggingIn] = useState(false);

    // -- Register state --
    const [regForm, setRegForm] = useState({ name: "", email: "", phone: "", password: "", confirm: "" });
    const [showRegPwd, setShowRegPwd] = useState(false);
    const [registering, setRegistering] = useState(false);
    const [regOtp, setRegOtp] = useState("");
    const [regVerifying, setRegVerifying] = useState(false);
    const [regResendCooldown, setRegResendCooldown] = useState(0);

    // -- Forgot password state --
    const [forgotEmail, setForgotEmail] = useState("");
    const [forgotSending, setForgotSending] = useState(false);
    const [forgotOtp, setForgotOtp] = useState("");
    const [forgotVerifying, setForgotVerifying] = useState(false);
    const [forgotResendCooldown, setForgotResendCooldown] = useState(0);
    const [resetToken, setResetToken] = useState("");
    const [resetForm, setResetForm] = useState({ password: "", confirm: "" });
    const [resetting, setResetting] = useState(false);
    const [showResetPwd, setShowResetPwd] = useState(false);

    // Fetch admin-configured method list when modal opens
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        axios.get(`${API}/settings/public`).then(r => {
            if (cancelled) return;
            const m = Array.isArray(r.data?.auth_methods) ? r.data.auth_methods : null;
            const resolved = (m && m.length) ? m : ["google", "phone"];
            _authMethodsCache = resolved;
            setAuthMethods(resolved);
            // Pull boutique contact email so the help popover always shows
            // the current contact (admin can change it in /config/settings).
            const ce = r.data?.contact_email || "";
            if (ce) setContactEmail(ce);
        }).catch(() => {
            if (cancelled) return;
            const fallback = ["google", "phone"];
            _authMethodsCache = fallback;
            setAuthMethods(fallback);
        });
        return () => { cancelled = true; };
    }, [open]);

    // Render sign-in options OPTIMISTICALLY from the default set until the admin
    // config resolves, so the Google button is on-screen from the FIRST paint.
    // Previously these were gated on `authMethods !== null`, so on a cold open
    // (before /settings/public returned) the modal showed only email+password
    // for 2-3s -- reading as "password only" and costing sign-ins. The
    // open-effect above reconciles; the default equals the fallback, so no
    // flash in the common case.
    const effectiveMethods = authMethods ?? ["google", "phone"];
    const googleEnabled = effectiveMethods.includes("google");
    const phoneEnabled = effectiveMethods.includes("phone") && isPhoneAuthConfigured();

    // If modal re-opened because a phone session is incomplete, jump to profile
    useEffect(() => {
        if (open && user?.needs_profile && user?.phone) {
            const ph = String(user.phone).replace(/^\+?91/, "");
            setPhone(ph);
            setSignup((s) => ({ ...s, name: user.name || "" }));
            setStage("profile");
        }
    }, [open, user]);

    // Reset state every time we close
    useEffect(() => {
        if (!open) {
            setStage("login");
            setPhone(""); setOtp(""); setSending(false); setVerifying(false);
            setMergeCandidate(null); setMergeChoosing(false);
            pendingIdTokenRef.current = null;
            setSignup({ name: "", email: "", address_line: "", city: "", state: "", pincode: "" });
            setSavingProfile(false);
            setLoginForm({ email: "", password: "" }); setShowPwd(false); setLoggingIn(false);
            setRegForm({ name: "", email: "", phone: "", password: "", confirm: "" });
            setShowRegPwd(false); setRegistering(false); setRegOtp(""); setRegVerifying(false); setRegResendCooldown(0);
            setForgotEmail(""); setForgotSending(false); setForgotOtp(""); setForgotVerifying(false);
            setForgotResendCooldown(0); setResetToken(""); setResetForm({ password: "", confirm: "" });
            setResetting(false); setShowResetPwd(false);
            setHelpOpen(false);
            confirmRef.current = null;
            resetRecaptcha(RECAPTCHA_ID);
        }
    }, [open]);

    // Cooldown countdowns
    useEffect(() => {
        if (resendCooldown <= 0) return;
        const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
        return () => clearTimeout(t);
    }, [resendCooldown]);
    useEffect(() => {
        if (regResendCooldown <= 0) return;
        const t = setTimeout(() => setRegResendCooldown((s) => s - 1), 1000);
        return () => clearTimeout(t);
    }, [regResendCooldown]);
    useEffect(() => {
        if (forgotResendCooldown <= 0) return;
        const t = setTimeout(() => setForgotResendCooldown((s) => s - 1), 1000);
        return () => clearTimeout(t);
    }, [forgotResendCooldown]);

    // ---------- Google ----------
    const [googleStarting, setGoogleStarting] = useState(false);
    const handleGoogle = () => {
        setGoogleStarting(true);
        setTimeout(() => { startGoogleLogin(); }, 350);
    };

    // ---------- Email + Password Login ----------
    const handlePasswordLogin = async (e) => {
        e?.preventDefault?.();
        clearBanners();
        const email = (loginForm.email || "").trim().toLowerCase();
        const password = loginForm.password || "";
        if (!isValidEmail(email)) { setFormError("Please enter a valid email."); return; }
        if (password.length < 1) { setFormError("Please enter your password."); return; }
        setLoggingIn(true);
        try {
            await axios.post(`${API}/auth/login/password`, { email, password }, { withCredentials: true });
            await refresh();
            onClose?.();
        } catch (e2) {
            setFormError(safeDetail(e2, "Could not sign in. Please try again."));
        } finally {
            setLoggingIn(false);
        }
    };

    // ---------- Register (init) ----------
    const handleRegisterInit = async (e) => {
        e?.preventDefault?.();
        clearBanners();
        const name = (regForm.name || "").trim();
        const email = (regForm.email || "").trim().toLowerCase();
        const phoneVal = (regForm.phone || "").trim();
        const password = regForm.password || "";
        const confirm = regForm.confirm || "";
        if (name.length < 2) { setFormError("Please enter your full name."); return; }
        if (!isValidEmail(email)) { setFormError("Please enter a valid email."); return; }
        if (password.length < 8) { setFormError("Password must be at least 8 characters."); return; }
        if (!/[A-Za-z]/.test(password)) { setFormError("Password must include at least one letter."); return; }
        if (!/[0-9]/.test(password)) { setFormError("Password must include at least one number."); return; }
        if (password !== confirm) { setFormError("Passwords do not match."); return; }
        setRegistering(true);
        try {
            await axios.post(`${API}/auth/register/init`, {
                name, email,
                phone: phoneVal || undefined,
                password, confirm_password: confirm,
            });
            setStage("register_verify");
            setRegResendCooldown(60);
        } catch (e2) {
            setFormError(safeDetail(e2, "Could not start registration. Please try again."));
        } finally {
            setRegistering(false);
        }
    };

    const handleRegisterVerify = async (e) => {
        e?.preventDefault?.();
        clearBanners();
        const code = (regOtp || "").replace(/\D/g, "");
        if (code.length !== 6) { setFormError("Please enter the 6-digit code."); return; }
        setRegVerifying(true);
        try {
            await axios.post(`${API}/auth/register/verify`, {
                email: regForm.email.trim().toLowerCase(),
                code,
            }, { withCredentials: true });
            await refresh();
            onClose?.();
        } catch (e2) {
            setFormError(safeDetail(e2, "Could not verify code. Please try again."));
        } finally {
            setRegVerifying(false);
        }
    };

    const handleRegisterResend = async () => {
        if (regResendCooldown > 0 || registering) return;
        clearBanners();
        setRegistering(true);
        try {
            await axios.post(`${API}/auth/register/init`, {
                name: regForm.name, email: regForm.email,
                phone: (regForm.phone || "").trim() || undefined,
                password: regForm.password, confirm_password: regForm.confirm,
            });
            setRegResendCooldown(60);
            setFormFlash("New code sent to your email.");
        } catch (e2) {
            setFormError(safeDetail(e2, "Could not resend code."));
        } finally {
            setRegistering(false);
        }
    };

    // ---------- Forgot Password ----------
    const handleForgotInit = async (e) => {
        e?.preventDefault?.();
        clearBanners();
        const email = (forgotEmail || "").trim().toLowerCase();
        if (!isValidEmail(email)) { setFormError("Please enter a valid email."); return; }
        setForgotSending(true);
        try {
            await axios.post(`${API}/auth/forgot/init`, { email });
            setStage("forgot_verify");
            setForgotResendCooldown(60);
        } catch (e2) {
            setFormError(safeDetail(e2, "Could not send reset code. Please try again."));
        } finally {
            setForgotSending(false);
        }
    };

    const handleForgotVerify = async (e) => {
        e?.preventDefault?.();
        clearBanners();
        const code = (forgotOtp || "").replace(/\D/g, "");
        if (code.length !== 6) { setFormError("Please enter the 6-digit code."); return; }
        setForgotVerifying(true);
        try {
            const r = await axios.post(`${API}/auth/forgot/verify`, {
                email: forgotEmail.trim().toLowerCase(), code,
            });
            setResetToken(r.data?.reset_token || "");
            setStage("forgot_reset");
        } catch (e2) {
            setFormError(safeDetail(e2, "Could not verify code."));
        } finally {
            setForgotVerifying(false);
        }
    };

    const handleForgotResend = async () => {
        if (forgotResendCooldown > 0 || forgotSending) return;
        clearBanners();
        setForgotSending(true);
        try {
            await axios.post(`${API}/auth/forgot/init`, { email: forgotEmail.trim().toLowerCase() });
            setForgotResendCooldown(60);
            setFormFlash("New code sent to your email.");
        } catch (e2) {
            setFormError(safeDetail(e2, "Could not resend code."));
        } finally {
            setForgotSending(false);
        }
    };

    const handleForgotReset = async (e) => {
        e?.preventDefault?.();
        clearBanners();
        const password = resetForm.password || "";
        const confirm = resetForm.confirm || "";
        if (password.length < 8) { setFormError("Password must be at least 8 characters."); return; }
        if (!/[A-Za-z]/.test(password)) { setFormError("Password must include at least one letter."); return; }
        if (!/[0-9]/.test(password)) { setFormError("Password must include at least one number."); return; }
        if (password !== confirm) { setFormError("Passwords do not match."); return; }
        setResetting(true);
        try {
            await axios.post(`${API}/auth/forgot/reset`, {
                reset_token: resetToken,
                new_password: password,
                confirm_password: confirm,
            });
            // Go back to login stage with email prefilled + show inline
            // success banner there. We set the flash AFTER changing stage so
            // the stage-change useEffect (which clears banners) doesn't wipe
            // it. Order matters here.
            setLoginForm({ email: forgotEmail.trim().toLowerCase(), password: "" });
            setStage("login");
            // Use a microtask to ensure setStage's clear-banners effect runs
            // first, then our success flash lands cleanly on the login screen.
            setTimeout(() => setFormFlash("Password updated. Please sign in with your new password."), 0);
        } catch (e2) {
            setFormError(safeDetail(e2, "Could not update password."));
        } finally {
            setResetting(false);
        }
    };

    // ---------- Phone OTP (legacy, dormant when Firebase setting OFF) ----------
    const phoneDigits = phone.replace(/\D/g, "");
    const phoneValid = phoneDigits.length === 10;
    const e164 = phoneValid ? `+91${phoneDigits}` : "";

    const sendOtp = async () => {
        if (!phoneValid || sending) return;
        if (!isPhoneAuthConfigured()) { setFormError("Phone sign-in is not configured yet. Please use email."); return; }
        setSending(true);
        try {
            const auth = getFirebaseAuth();
            if (!auth) throw new Error("Firebase init failed");
            const verifier = getRecaptchaVerifier(RECAPTCHA_ID);
            const result = await signInWithPhoneNumber(auth, e164, verifier);
            confirmRef.current = result;
            setStage("otp");
            setResendCooldown(30);
            setFormFlash(`Code sent to ${e164}`);
        } catch (e) {
            const code = String(e?.code || "");
            let msg = "Could not send code. Please try again.";
            if (code.includes("invalid-phone")) msg = "That doesn't look like a valid phone number.";
            else if (code.includes("too-many-requests")) msg = "Too many attempts. Try again in a few minutes.";
            else if (code.includes("quota")) msg = "Daily SMS quota reached. Please try Google sign-in.";
            else if (code) msg = `Could not send code · ${code.replace(/^auth\//, "")}`;
            setFormError(msg);
            resetRecaptcha(RECAPTCHA_ID);
        } finally {
            setSending(false);
        }
    };

    const verifyOtp = async () => {
        if (otp.replace(/\D/g, "").length !== 6 || verifying) return;
        setVerifying(true);
        try {
            const cr = confirmRef.current;
            if (!cr) throw new Error("Session lost — please request a new code");
            const result = await cr.confirm(otp.replace(/\D/g, ""));
            const idToken = await result.user.getIdToken();
            const r = await axios.post(`${API}/auth/phone/verify`, { id_token: idToken }, { withCredentials: true });
            if (r.data?.merge_required) {
                pendingIdTokenRef.current = idToken;
                setMergeCandidate(r.data.candidate || {});
                setStage("merge");
            } else if (r.data?.needs_profile) {
                setSignup((s) => ({ ...s, name: r.data?.name || "" }));
                setStage("profile");
            } else {
                await refresh();
                onClose?.();
            }
        } catch (e) {
            setFormError(safeDetail(e, "Incorrect code. Try again."));
        } finally {
            setVerifying(false);
        }
    };

    const handleMergeDecision = async (decision) => {
        if (mergeChoosing) return;
        const idToken = pendingIdTokenRef.current;
        if (!idToken) { setFormError("Session expired — please try again."); return; }
        setMergeChoosing(true);
        try {
            const r = await axios.post(`${API}/auth/phone/merge-confirm`,
                { id_token: idToken, decision }, { withCredentials: true });
            if (r.data?.needs_profile) {
                setSignup((s) => ({ ...s, name: r.data?.name || "" }));
                setStage("profile");
            } else {
                await refresh();
                onClose?.();
            }
        } catch (e) {
            setFormError(safeDetail(e, "Something went wrong. Please try again."));
        } finally {
            setMergeChoosing(false);
        }
    };

    const submitProfile = async () => {
        const name = signup.name.trim();
        const email = signup.email.trim().toLowerCase();
        if (name.length < 2) { setFormError("Please enter your full name"); return; }
        if (!isValidEmail(email)) { setFormError("Please enter a valid email"); return; }
        setSavingProfile(true);
        try {
            const payload = { name, email };
            for (const k of ["address_line", "city", "state", "pincode"]) {
                if ((signup[k] || "").trim()) payload[k] = signup[k].trim();
            }
            await axios.post(`${API}/auth/complete-profile`, payload, { withCredentials: true });
            await refresh();
            onClose?.();
        } catch (e) {
            setFormError(safeDetail(e, "Could not save profile. Please try again."));
        } finally {
            setSavingProfile(false);
        }
    };

    const handleClose = async () => {
        if (stage === "profile") {
            try { await logout(); } catch { /* noop */ }
        }
        onClose?.();
    };

    if (!open) return null;

    // --- Stage metadata for title/subtitle ---
    const titleFor = {
        login: "Sign in to JnS",
        register: "Create your account",
        register_verify: "Verify your email",
        forgot: "Forgot password?",
        forgot_verify: "Verify your email",
        forgot_reset: "Set a new password",
        phone: "Enter your phone",
        otp: "Enter the 6-digit code",
        merge: "We found your account",
        profile: "One last step",
    };
    const subtitleFor = {
        login: "Welcome back — sign in to continue your boutique journey.",
        register: "Join JnS Creation for exclusive handpicked styles.",
        register_verify: `We sent a 6-digit code to ${regForm.email || "your email"}`,
        forgot: "We'll send a 6-digit code to verify it's you.",
        forgot_verify: `We sent a code to ${forgotEmail || "your email"}`,
        forgot_reset: "Choose a strong password you'll remember.",
        phone: "We'll text you a one-time code.",
        otp: `Sent to ${e164}`,
        merge: "This phone matches an existing account.",
        profile: "Tell us your name and email so we can send order updates.",
    };

    const showBackBtn = !["login", "phone", "otp", "merge", "profile"].includes(stage) || (stage === "phone");

    return createPortal(
        <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center" data-testid="auth-modal">
            <div className="absolute inset-0 bg-ink/55 backdrop-blur-sm animate-in fade-in duration-200" onClick={handleClose} />

            <div
                className="relative bg-white w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl shadow-2xl p-6 sm:p-8 max-h-[92vh] overflow-y-auto animate-in slide-in-from-bottom-4 sm:zoom-in-95 duration-300"
                onClick={(e) => e.stopPropagation()}
                data-testid={`auth-stage-${stage}`}
            >
                {/* Mobile drag handle */}
                <div className="sm:hidden flex justify-center -mt-2 mb-3" aria-hidden="true">
                    <span className="block h-1.5 w-12 rounded-full bg-stone-300" />
                </div>

                {/* Close */}
                <button
                    onClick={handleClose}
                    aria-label="Close"
                    data-testid="auth-modal-close"
                    className="absolute top-3 right-3 w-9 h-9 rounded-full hover:bg-stone-100 inline-flex items-center justify-center text-stone-500 transition-colors"
                >
                    <X size={18} />
                </button>

                {/* Help icon → contact popover. Anchored under the close button
                    so it's always reachable regardless of stage. Opens an
                    inline popover with the boutique's admin contact email so
                    users can write in if anything goes wrong with sign-in. */}
                {contactEmail && (
                    <div className="absolute top-3 right-14 z-10">
                        <button
                            type="button"
                            onClick={() => setHelpOpen(s => !s)}
                            aria-label="Need help signing in?"
                            aria-expanded={helpOpen}
                            data-testid="auth-help-btn"
                            className={`w-9 h-9 rounded-full inline-flex items-center justify-center transition-colors ${helpOpen ? "bg-rosegold/10 text-rosegold" : "text-stone-500 hover:bg-stone-100"}`}
                        >
                            <HelpCircle size={18} />
                        </button>
                        {helpOpen && (
                            <div
                                role="dialog"
                                aria-label="Contact help"
                                data-testid="auth-help-popover"
                                className="absolute top-11 right-0 w-64 rounded-xl border border-stone-200 bg-white shadow-xl p-3.5 text-left animate-in fade-in slide-in-from-top-2 duration-150"
                            >
                                <p className="text-[11px] font-bold uppercase tracking-wider text-ink-soft mb-1">Need help signing in?</p>
                                <p className="text-[12px] text-ink-soft leading-relaxed mb-2">
                                    Write to our boutique team and we'll get back to you within a day.
                                </p>
                                <a
                                    href={`mailto:${contactEmail}?subject=Sign-in%20help%20-%20JnS%20Creation`}
                                    data-testid="auth-help-email"
                                    className="inline-flex items-center gap-1.5 text-[12px] font-bold text-rosegold hover:text-rosegold-dark hover:underline break-all"
                                >
                                    <Mail size={13} /> {contactEmail}
                                </a>
                            </div>
                        )}
                    </div>
                )}

                {/* Back */}
                {showBackBtn && (
                    <button
                        onClick={() => {
                            if (stage === "phone" || stage === "register" || stage === "forgot") setStage("login");
                            else if (stage === "register_verify") setStage("register");
                            else if (stage === "forgot_verify") setStage("forgot");
                            else if (stage === "forgot_reset") setStage("forgot_verify");
                            else setStage("login");
                            setOtp(""); setRegOtp(""); setForgotOtp("");
                            confirmRef.current = null;
                            resetRecaptcha(RECAPTCHA_ID);
                        }}
                        aria-label="Back"
                        data-testid="auth-modal-back"
                        className="absolute top-3 left-3 w-9 h-9 rounded-full hover:bg-stone-100 inline-flex items-center justify-center text-stone-500 transition-colors"
                    >
                        <ArrowLeft size={18} />
                    </button>
                )}

                {/* Header */}
                <div className="text-center mb-6 pt-1">
                    {/* Brand logo above title for premium feel */}
                    <div className="inline-flex items-center justify-center mb-3">
                        <img
                            src={brandLogo || "/jns-logo.png"}
                            alt="JnS Creation"
                            className="h-14 sm:h-16 w-auto object-contain"
                            data-testid="auth-modal-logo"
                        />
                    </div>
                    <h2 className="font-heading text-2xl text-ink leading-tight" data-testid="auth-modal-title">
                        {titleFor[stage] || "Sign in"}
                    </h2>
                    <p className="text-[12px] text-ink-soft mt-1.5 leading-relaxed px-2">
                        {subtitleFor[stage] || ""}
                    </p>
                </div>

                {/* ============== LOGIN (default) ============== */}
                {stage === "login" && (
                    <form onSubmit={handlePasswordLogin} className="space-y-3" data-testid="auth-login-form">
                        {formError && <Banner kind="error" message={formError} />}
                        {formFlash && <Banner kind="success" message={formFlash} />}
                        <FieldEmail
                            value={loginForm.email}
                            onChange={(v) => setLoginForm(f => ({ ...f, email: v }))}
                            testid="auth-login-email"
                        />
                        <FieldPassword
                            value={loginForm.password}
                            onChange={(v) => setLoginForm(f => ({ ...f, password: v }))}
                            show={showPwd}
                            onToggleShow={() => setShowPwd(s => !s)}
                            testid="auth-login-password"
                            autoComplete="current-password"
                        />
                        <div className="flex justify-end -mt-1">
                            <button
                                type="button"
                                onClick={() => { setForgotEmail(loginForm.email); setStage("forgot"); }}
                                data-testid="auth-forgot-link"
                                className="text-[12px] font-semibold text-rosegold hover:text-rosegold-dark hover:underline transition-colors"
                            >
                                Forgot password?
                            </button>
                        </div>
                        <button
                            type="submit"
                            disabled={loggingIn}
                            data-testid="auth-login-submit"
                            className="w-full h-12 rounded-md btn-primary inline-flex items-center justify-center gap-2 text-sm font-bold uppercase tracking-wider disabled:opacity-50 transition-all"
                        >
                            {loggingIn ? <Loader2 size={16} className="animate-spin" /> : null}
                            {loggingIn ? "Signing in…" : "Sign in"}
                        </button>

                        {/* Divider + Google + (legacy) Phone */}
                        {(googleEnabled || phoneEnabled) && (
                            <>
                                <Divider label="OR" />
                                {googleEnabled && (
                                    <button
                                        type="button"
                                        onClick={handleGoogle}
                                        disabled={googleStarting}
                                        data-testid="auth-google-btn"
                                        className="w-full h-12 rounded-md border-2 border-stone-300 bg-white shadow-[0_7px_20px_-10px_rgba(107,44,57,0.5)] hover:border-ink hover:bg-stone-50 hover:shadow-[0_10px_26px_-10px_rgba(107,44,57,0.6)] inline-flex items-center justify-center gap-2.5 text-sm font-semibold text-ink transition-all disabled:opacity-70"
                                    >
                                        {googleStarting ? (
                                            <>
                                                <div className="w-4 h-4 rounded-full border-2 border-stone-300 border-t-ink animate-spin" />
                                                Connecting to Google…
                                            </>
                                        ) : (
                                            <><GoogleGlyph /> Continue with Google</>
                                        )}
                                    </button>
                                )}
                                {phoneEnabled && (
                                    <button
                                        type="button"
                                        onClick={() => setStage("phone")}
                                        data-testid="auth-phone-btn"
                                        className="w-full h-12 rounded-md border-2 border-stone-300 bg-white hover:border-ink hover:bg-stone-50 inline-flex items-center justify-center gap-2.5 text-sm font-semibold text-ink transition-all"
                                    >
                                        <Phone size={16} /> Continue with Phone
                                    </button>
                                )}
                            </>
                        )}

                        {/* Footer link */}
                        <div className="text-center pt-3" data-testid="auth-register-link">
                            <span className="text-[13px] text-ink-soft">New to JnS? </span>
                            <button
                                type="button"
                                onClick={() => setStage("register")}
                                className="text-[13px] font-bold text-rosegold hover:text-rosegold-dark hover:underline transition-colors"
                            >
                                Create account
                            </button>
                        </div>
                    </form>
                )}

                {/* ============== REGISTER ============== */}
                {stage === "register" && (
                    <form onSubmit={handleRegisterInit} className="space-y-3" data-testid="auth-register-form">
                        {formError && <Banner kind="error" message={formError} />}
                        {formFlash && <Banner kind="success" message={formFlash} />}
                        <Field
                            label="Full name"
                            type="text"
                            autoComplete="name"
                            value={regForm.name}
                            onChange={(v) => setRegForm(f => ({ ...f, name: v.slice(0, 80) }))}
                            placeholder="Your full name"
                            testid="auth-register-name"
                            autoFocus
                        />
                        <FieldEmail
                            value={regForm.email}
                            onChange={(v) => setRegForm(f => ({ ...f, email: v }))}
                            testid="auth-register-email"
                        />
                        <div>
                            <label className="text-[11px] font-bold uppercase tracking-wider text-ink-soft block mb-1.5">
                                Phone <span className="text-ink-soft/70 font-normal normal-case">(optional)</span>
                            </label>
                            <div className="flex items-stretch gap-2">
                                <div className="inline-flex items-center px-3 h-11 rounded-md border-2 border-stone-300 bg-stone-50 text-sm font-semibold text-ink">+91</div>
                                <input
                                    type="tel"
                                    inputMode="numeric"
                                    autoComplete="tel"
                                    maxLength={10}
                                    value={regForm.phone}
                                    onChange={(e) => setRegForm(f => ({ ...f, phone: e.target.value.replace(/\D/g, "").slice(0, 10) }))}
                                    placeholder="98765 43210"
                                    data-testid="auth-register-phone"
                                    className="flex-1 h-11 px-3 rounded-md border-2 border-stone-300 bg-white text-base focus:outline-none focus:border-rosegold focus:ring-2 focus:ring-rosegold/20 transition-all"
                                />
                            </div>
                        </div>
                        <FieldPassword
                            label="Password"
                            value={regForm.password}
                            onChange={(v) => setRegForm(f => ({ ...f, password: v }))}
                            show={showRegPwd}
                            onToggleShow={() => setShowRegPwd(s => !s)}
                            testid="auth-register-password"
                            autoComplete="new-password"
                            hint="Min 8 characters, with at least 1 letter and 1 number."
                        />
                        <FieldPassword
                            label="Confirm password"
                            value={regForm.confirm}
                            onChange={(v) => setRegForm(f => ({ ...f, confirm: v }))}
                            show={showRegPwd}
                            onToggleShow={() => setShowRegPwd(s => !s)}
                            testid="auth-register-confirm"
                            autoComplete="new-password"
                        />
                        <button
                            type="submit"
                            disabled={registering}
                            data-testid="auth-register-submit"
                            className="w-full h-12 rounded-md btn-primary inline-flex items-center justify-center gap-2 text-sm font-bold uppercase tracking-wider disabled:opacity-50 transition-all mt-2"
                        >
                            {registering ? <Loader2 size={16} className="animate-spin" /> : null}
                            {registering ? "Sending code…" : "Send verification code"}
                        </button>
                        <p className="text-[11px] text-ink-soft text-center pt-1">
                            Already have an account?{" "}
                            <button type="button" onClick={() => setStage("login")} className="font-bold text-rosegold hover:underline">Sign in</button>
                        </p>
                    </form>
                )}

                {/* ============== REGISTER VERIFY (OTP) ============== */}
                {stage === "register_verify" && (
                    <form onSubmit={handleRegisterVerify} className="space-y-4" data-testid="auth-register-verify-form">
                        {formError && <Banner kind="error" message={formError} />}
                        {formFlash && <Banner kind="success" message={formFlash} />}
                        <OTPInput value={regOtp} onChange={setRegOtp} testid="auth-register-otp" />
                        <button
                            type="submit"
                            disabled={regOtp.replace(/\D/g, "").length !== 6 || regVerifying}
                            data-testid="auth-register-verify-submit"
                            className="w-full h-12 rounded-md btn-primary inline-flex items-center justify-center gap-2 text-sm font-bold uppercase tracking-wider disabled:opacity-50 transition-all"
                        >
                            {regVerifying ? <Loader2 size={16} className="animate-spin" /> : null}
                            {regVerifying ? "Creating account…" : "Verify & create account"}
                        </button>
                        <div className="flex items-center justify-between text-xs text-ink-soft">
                            <button
                                type="button"
                                onClick={() => setStage("register")}
                                className="font-bold text-rosegold hover:underline"
                                data-testid="auth-register-change-email"
                            >
                                Change email
                            </button>
                            <button
                                type="button"
                                onClick={handleRegisterResend}
                                disabled={regResendCooldown > 0 || registering}
                                data-testid="auth-register-resend"
                                className="font-bold text-rosegold hover:underline disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
                            >
                                {regResendCooldown > 0 ? `Resend in ${regResendCooldown}s` : "Resend code"}
                            </button>
                        </div>
                    </form>
                )}

                {/* ============== FORGOT ============== */}
                {stage === "forgot" && (
                    <form onSubmit={handleForgotInit} className="space-y-3" data-testid="auth-forgot-form">
                        {formError && <Banner kind="error" message={formError} />}
                        {formFlash && <Banner kind="success" message={formFlash} />}
                        <FieldEmail
                            value={forgotEmail}
                            onChange={setForgotEmail}
                            testid="auth-forgot-email"
                            autoFocus
                        />
                        <button
                            type="submit"
                            disabled={forgotSending}
                            data-testid="auth-forgot-submit"
                            className="w-full h-12 rounded-md btn-primary inline-flex items-center justify-center gap-2 text-sm font-bold uppercase tracking-wider disabled:opacity-50 transition-all"
                        >
                            {forgotSending ? <Loader2 size={16} className="animate-spin" /> : null}
                            {forgotSending ? "Sending code…" : "Send verification code"}
                        </button>
                        <p className="text-[11px] text-ink-soft text-center pt-1">
                            Remembered it?{" "}
                            <button type="button" onClick={() => setStage("login")} className="font-bold text-rosegold hover:underline">Back to sign in</button>
                        </p>
                    </form>
                )}

                {/* ============== FORGOT VERIFY ============== */}
                {stage === "forgot_verify" && (
                    <form onSubmit={handleForgotVerify} className="space-y-4" data-testid="auth-forgot-verify-form">
                        {formError && <Banner kind="error" message={formError} />}
                        {formFlash && <Banner kind="success" message={formFlash} />}
                        <OTPInput value={forgotOtp} onChange={setForgotOtp} testid="auth-forgot-otp" />
                        <button
                            type="submit"
                            disabled={forgotOtp.replace(/\D/g, "").length !== 6 || forgotVerifying}
                            data-testid="auth-forgot-verify-submit"
                            className="w-full h-12 rounded-md btn-primary inline-flex items-center justify-center gap-2 text-sm font-bold uppercase tracking-wider disabled:opacity-50 transition-all"
                        >
                            {forgotVerifying ? <Loader2 size={16} className="animate-spin" /> : null}
                            {forgotVerifying ? "Verifying…" : "Verify"}
                        </button>
                        <div className="flex items-center justify-between text-xs text-ink-soft">
                            <button
                                type="button"
                                onClick={() => setStage("forgot")}
                                className="font-bold text-rosegold hover:underline"
                            >
                                Change email
                            </button>
                            <button
                                type="button"
                                onClick={handleForgotResend}
                                disabled={forgotResendCooldown > 0 || forgotSending}
                                data-testid="auth-forgot-resend"
                                className="font-bold text-rosegold hover:underline disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
                            >
                                {forgotResendCooldown > 0 ? `Resend in ${forgotResendCooldown}s` : "Resend code"}
                            </button>
                        </div>
                    </form>
                )}

                {/* ============== FORGOT RESET (new password) ============== */}
                {stage === "forgot_reset" && (
                    <form onSubmit={handleForgotReset} className="space-y-3" data-testid="auth-forgot-reset-form">
                        {formError && <Banner kind="error" message={formError} />}
                        {formFlash && <Banner kind="success" message={formFlash} />}
                        <div className="rounded-lg bg-gradient-to-br from-emerald-50 to-white border border-emerald-200/60 p-3 flex items-start gap-2.5">
                            <ShieldCheck size={18} className="text-emerald-600 shrink-0 mt-0.5" />
                            <p className="text-[12px] text-emerald-900 leading-relaxed">
                                Email verified. Choose a new password to finish.
                            </p>
                        </div>
                        <FieldPassword
                            label="New password"
                            value={resetForm.password}
                            onChange={(v) => setResetForm(f => ({ ...f, password: v }))}
                            show={showResetPwd}
                            onToggleShow={() => setShowResetPwd(s => !s)}
                            testid="auth-forgot-reset-password"
                            autoComplete="new-password"
                            autoFocus
                            hint="Min 8 characters, with at least 1 letter and 1 number."
                        />
                        <FieldPassword
                            label="Confirm new password"
                            value={resetForm.confirm}
                            onChange={(v) => setResetForm(f => ({ ...f, confirm: v }))}
                            show={showResetPwd}
                            onToggleShow={() => setShowResetPwd(s => !s)}
                            testid="auth-forgot-reset-confirm"
                            autoComplete="new-password"
                        />
                        <button
                            type="submit"
                            disabled={resetting}
                            data-testid="auth-forgot-reset-submit"
                            className="w-full h-12 rounded-md btn-primary inline-flex items-center justify-center gap-2 text-sm font-bold uppercase tracking-wider disabled:opacity-50 transition-all mt-2"
                        >
                            {resetting ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                            {resetting ? "Updating password…" : "Save new password"}
                        </button>
                    </form>
                )}

                {/* ============== Phone OTP (legacy stages — dormant unless re-enabled) ============== */}
                {stage === "phone" && (
                    <div className="space-y-3" data-testid="auth-phone-stage">
                        {formError && <Banner kind="error" message={formError} />}
                        {formFlash && <Banner kind="success" message={formFlash} />}
                        <div className="flex items-stretch gap-2">
                            <div className="inline-flex items-center px-3 h-12 rounded-md border-2 border-stone-300 bg-stone-50 text-sm font-semibold text-ink">+91</div>
                            <input
                                type="tel"
                                inputMode="numeric"
                                autoComplete="tel"
                                maxLength={10}
                                value={phone}
                                onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                                placeholder="98765 43210"
                                data-testid="auth-phone-input"
                                className="flex-1 h-12 px-3 rounded-md border-2 border-stone-300 bg-white text-base focus:outline-none focus:border-rosegold focus:ring-2 focus:ring-rosegold/20"
                                autoFocus
                            />
                        </div>
                        <button
                            type="button"
                            onClick={sendOtp}
                            disabled={!phoneValid || sending}
                            data-testid="auth-send-otp"
                            className="w-full h-12 rounded-md btn-primary inline-flex items-center justify-center gap-2 text-sm font-bold uppercase tracking-wider disabled:opacity-50"
                        >
                            {sending ? <Loader2 size={16} className="animate-spin" /> : null}
                            {sending ? "Sending…" : "Send code"}
                        </button>
                    </div>
                )}

                {stage === "otp" && (
                    <div className="space-y-3" data-testid="auth-otp-stage">
                        {formError && <Banner kind="error" message={formError} />}
                        {formFlash && <Banner kind="success" message={formFlash} />}
                        <OTPInput value={otp} onChange={setOtp} testid="auth-otp-input" />
                        <button
                            type="button"
                            onClick={verifyOtp}
                            disabled={otp.length !== 6 || verifying}
                            data-testid="auth-verify-otp"
                            className="w-full h-12 rounded-md btn-primary inline-flex items-center justify-center gap-2 text-sm font-bold uppercase tracking-wider disabled:opacity-50"
                        >
                            {verifying ? <Loader2 size={16} className="animate-spin" /> : null}
                            {verifying ? "Verifying…" : "Verify & sign in"}
                        </button>
                        <div className="text-center text-xs text-ink-soft">
                            Didn't get it?{" "}
                            <button
                                type="button"
                                onClick={sendOtp}
                                disabled={resendCooldown > 0 || sending}
                                data-testid="auth-resend-otp"
                                className="font-bold text-rosegold disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
                            </button>
                        </div>
                    </div>
                )}

                {stage === "merge" && mergeCandidate && (
                    <div className="space-y-3" data-testid="auth-merge-stage">
                        {formError && <Banner kind="error" message={formError} />}
                        {formFlash && <Banner kind="success" message={formFlash} />}
                        <div className="rounded-lg border-2 border-rosegold/40 bg-ivory-soft p-4 text-center">
                            <p className="text-[11px] uppercase tracking-wider text-ink-soft font-bold mb-2">Existing account</p>
                            {mergeCandidate.masked_name && <p className="font-heading text-lg text-ink">{mergeCandidate.masked_name}</p>}
                            {mergeCandidate.masked_email && <p className="text-sm text-ink-soft mt-0.5">{mergeCandidate.masked_email}</p>}
                            <p className="text-[11px] text-ink-soft mt-2">
                                Originally signed up with <span className="font-semibold text-ink">{mergeCandidate.signup_method === "phone" ? "phone" : "Google"}</span>
                            </p>
                        </div>
                        <button type="button" onClick={() => handleMergeDecision("merge")} disabled={mergeChoosing}
                            data-testid="auth-merge-yes"
                            className="w-full h-12 rounded-md btn-primary inline-flex items-center justify-center gap-2 text-sm font-bold uppercase tracking-wider disabled:opacity-50">
                            {mergeChoosing ? <Loader2 size={16} className="animate-spin" /> : null}
                            Yes, sign me into that account
                        </button>
                        <button type="button" onClick={() => handleMergeDecision("new")} disabled={mergeChoosing}
                            data-testid="auth-merge-no"
                            className="w-full h-10 text-xs font-bold uppercase tracking-wider text-rosegold hover:underline disabled:opacity-50">
                            Not me — create a separate account
                        </button>
                    </div>
                )}

                {stage === "profile" && (
                    <div className="space-y-3" data-testid="auth-profile-stage">
                        {formError && <Banner kind="error" message={formError} />}
                        {formFlash && <Banner kind="success" message={formFlash} />}
                        <Field
                            label="Full name *"
                            type="text"
                            autoComplete="name"
                            value={signup.name}
                            onChange={(v) => setSignup(s => ({ ...s, name: v.slice(0, 80) }))}
                            placeholder="Your full name"
                            testid="auth-profile-name"
                            autoFocus
                        />
                        <FieldEmail
                            label="Email *"
                            value={signup.email}
                            onChange={(v) => setSignup(s => ({ ...s, email: v.slice(0, 120) }))}
                            testid="auth-profile-email"
                        />
                        <button type="button" onClick={submitProfile} disabled={savingProfile || !signup.name.trim() || !signup.email.trim()}
                            data-testid="auth-profile-submit"
                            className="w-full h-12 rounded-md btn-primary inline-flex items-center justify-center gap-2 text-sm font-bold uppercase tracking-wider disabled:opacity-50">
                            {savingProfile ? <Loader2 size={16} className="animate-spin" /> : null}
                            {savingProfile ? "Saving…" : "Finish & start shopping"}
                        </button>
                    </div>
                )}

                {/* Invisible reCAPTCHA container — required by Firebase Phone Auth */}
                <div id={RECAPTCHA_ID} />
            </div>
        </div>,
        document.body
    );
}

/* -------------------- Reusable bits -------------------- */
const Banner = ({ kind = "error", message, testid }) => {
    if (!message) return null;
    const styles = kind === "error"
        ? "bg-red-50 border-red-200 text-red-800"
        : "bg-emerald-50 border-emerald-200 text-emerald-900";
    const Icon = kind === "error" ? AlertCircle : CheckCircle2;
    const iconColor = kind === "error" ? "text-red-500" : "text-emerald-600";
    return (
        <div
            role="alert"
            data-testid={testid || `auth-banner-${kind}`}
            className={`flex items-start gap-2.5 rounded-lg border ${styles} px-3.5 py-2.5 text-[13px] leading-relaxed mb-3 animate-in fade-in slide-in-from-top-1 duration-200`}
        >
            <Icon size={16} className={`shrink-0 mt-0.5 ${iconColor}`} aria-hidden="true" />
            <span>{message}</span>
        </div>
    );
};

const Divider = ({ label }) => (
    <div className="relative my-4">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
            <div className="w-full border-t border-stone-200" />
        </div>
        <div className="relative flex justify-center">
            <span className="bg-white px-3 text-[10px] font-bold uppercase tracking-[0.22em] text-ink-soft">{label}</span>
        </div>
    </div>
);

const Field = ({ label, type = "text", value, onChange, placeholder, testid, autoComplete, autoFocus, hint }) => (
    <div>
        {label && <label className="text-[11px] font-bold uppercase tracking-wider text-ink-soft block mb-1.5">{label}</label>}
        <input
            type={type}
            autoComplete={autoComplete}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            data-testid={testid}
            autoFocus={autoFocus}
            className="w-full h-11 px-3 rounded-md border-2 border-stone-300 bg-white text-sm focus:outline-none focus:border-rosegold focus:ring-2 focus:ring-rosegold/20 transition-all"
        />
        {hint && <p className="text-[10px] text-ink-soft mt-1">{hint}</p>}
    </div>
);

const FieldEmail = ({ label = "Email", value, onChange, testid, autoFocus }) => (
    <div>
        <label className="text-[11px] font-bold uppercase tracking-wider text-ink-soft block mb-1.5">{label}</label>
        <div className="relative">
            <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft pointer-events-none" />
            <input
                type="email"
                inputMode="email"
                autoComplete="email"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder="you@example.com"
                data-testid={testid}
                autoFocus={autoFocus}
                className="w-full h-11 pl-10 pr-3 rounded-md border-2 border-stone-300 bg-white text-sm focus:outline-none focus:border-rosegold focus:ring-2 focus:ring-rosegold/20 transition-all"
            />
        </div>
    </div>
);

const FieldPassword = ({ label = "Password", value, onChange, show, onToggleShow, testid, autoComplete, autoFocus, hint }) => (
    <div>
        <label className="text-[11px] font-bold uppercase tracking-wider text-ink-soft block mb-1.5">{label}</label>
        <div className="relative">
            <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft pointer-events-none" />
            <input
                type={show ? "text" : "password"}
                autoComplete={autoComplete || "current-password"}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder="••••••••"
                data-testid={testid}
                autoFocus={autoFocus}
                className="w-full h-11 pl-10 pr-10 rounded-md border-2 border-stone-300 bg-white text-sm focus:outline-none focus:border-rosegold focus:ring-2 focus:ring-rosegold/20 transition-all"
            />
            <button
                type="button"
                onClick={onToggleShow}
                aria-label={show ? "Hide password" : "Show password"}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-ink-soft hover:text-ink hover:bg-stone-100 transition-colors"
                tabIndex={-1}
            >
                {show ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
        </div>
        {hint && <p className="text-[10px] text-ink-soft mt-1">{hint}</p>}
    </div>
);

const OTPInput = ({ value, onChange, testid }) => (
    <input
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
        placeholder="• • • • • •"
        data-testid={testid}
        className="w-full h-14 text-center font-mono text-2xl tracking-[0.5em] rounded-md border-2 border-stone-300 bg-white focus:outline-none focus:border-rosegold focus:ring-2 focus:ring-rosegold/20 transition-all"
        autoFocus
    />
);

// Multicolour Google "G" glyph
const GoogleGlyph = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.34-2.1V7.07H2.18A11 11 0 0 0 1 12c0 1.78.43 3.46 1.18 4.93l3.66-2.83z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.07.56 4.21 1.65l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.3 9.14 5.38 12 5.38z"/>
    </svg>
);
